'use client'

import { memo, useEffect, useState, useRef } from 'react'
import { Row, Col, LinearBlur } from '@repo/ui/components'
import { Icon, type IconNames } from '@repo/ui/components/icon'
import { Typography } from '@repo/ui/components/typography'
import { useTranslate } from '@repo/shared/hooks'
import { cn } from '@repo/ui/lib/utils'
import Image from 'next/image'
import { twMerge } from 'tailwind-merge'
import { Link, useRouter } from '@repo/shared/i18n/navigation'

import { usePathname } from '@repo/shared/i18n/navigation'
import { useIsDesktop } from '@repo/ui/hooks'
import { locales } from '@repo/shared/i18n/routing'
import { Transition } from '../transition'
import { MenuBarTradeModal } from './menu-bar.trade-modal'

function getPathnameRegex(pages: string[]) {
  return RegExp(
    `^(/(${locales.join('|')}))?(${pages
      .flatMap((p) => (p === '/' ? ['', '/'] : p.replace(/:[^/]+/g, '[^/]+')))
      .join('|')})/?$`,
    'i',
  )
}

export interface MenuBarConfig {
  extraMenuItems?: {
    href: string
    label: string
    description: string
    disabled: boolean
  }[]
  menuItems: {
    key: string
    icon: IconNames
    label: string
    src?: string
    href: string
  }[]
  activeMenuRoutes: string[]
  defaultShowFullMenu: boolean
  showTrigger: boolean
  triggerTitle?: string
}
export const MenuBar = memo(({ menuConfig }: { menuConfig: MenuBarConfig }) => {
  const _activeMenuRoutes = getPathnameRegex(menuConfig.activeMenuRoutes)
  const { t } = useTranslate()
  const { push } = useRouter()
  const pathname = usePathname()
  const activeMenuKey = menuConfig.menuItems.find((item) =>
    pathname.includes(item.href),
  )?.key

  const isActiveMenuRoute = _activeMenuRoutes.test(pathname)

  const [showFullMenu, setShowFullMenu] = useState(
    menuConfig.defaultShowFullMenu,
  )
  const [showTradeModal, setShowTradeModal] = useState(false)
  const [active, setActive] = useState<
    (typeof menuConfig.menuItems)[number]['key'] | undefined
  >(activeMenuKey)
  const tradeButtonRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isActiveMenuRoute) {
      setActive(activeMenuKey)

      setShowFullMenu(false)
    }
  }, [activeMenuKey, isActiveMenuRoute])

  const isDesktop = useIsDesktop()

  if (isDesktop || !isActiveMenuRoute) return null

  return (
    <Transition>
      <Col className='transition-all duration-700 fixed bottom-0 left-0 right-0  justify-between items-end w-full max-w-container-xl mx-auto gap-xs z-50'>
        {menuConfig.showTrigger && (
          <div ref={tradeButtonRef} className='p-xs'>
            <Row
              className={cn(
                'bg-base-radial-gradient transition-all duration-300 h-12  cursor-pointer rounded-full items-center justify-center px-xs flex gap-2xs',
              )}
              onClick={() => {
                setShowTradeModal(true)
              }}

              // onMouseEnter={() => {
              //   setShowFullMenu(true)
              // }}
              // onMouseLeave={() => {
              //   setShowFullMenu(false)
              // }}
            >
              <Icon
                name='ArrowDownLeftArrowUpRight'
                size='md'
                color='fill-neutral-surface'
              />
              <Typography
                variant='span'
                size='lg'
                weight='bold'
                color='text-neutral-surface'
                className={twMerge('whitespace-nowrap')}
              >
                {t(menuConfig.triggerTitle as keyof typeof t)}
              </Typography>
            </Row>
          </div>
        )}
        {menuConfig.showTrigger && (
          <MenuBarTradeModal
            open={showTradeModal}
            onOpenChange={setShowTradeModal}
            buttonRef={tradeButtonRef}
            items={menuConfig.extraMenuItems || []}
          />
        )}
        <Row
          className={twMerge(
            'gap-lg bg-neutral-main-80 backdrop-blur-[20px] shadow-md pb-sm px-md justify-around  h-18',
          )}
        >
          {menuConfig.menuItems.map((item) => {
            const isActive = active === item.key
            return (
              <Link href={item.href} key={item.key}>
                <Col
                  className={cn('items-center cursor-pointer')}
                  buttonClassName='min-w-6'
                >
                  {item.src && isActive ? (
                    <Image
                      src={item.src}
                      alt={item.label}
                      width={24}
                      height={24}
                    />
                  ) : (
                    <Icon
                      name={item.icon as IconNames}
                      size='md'
                      color='fill-neutral-surface-100'
                      className={cn(!isActive && 'opacity-40')}
                    />
                  )}
                  <Typography
                    variant='span'
                    size='2xs'
                    color={'text-neutral-main'}
                    className={twMerge(
                      'whitespace-nowrap',
                      !showFullMenu ? 'opacity-100' : 'opacity-0 hidden',
                      isActive ? 'opacity-100' : 'opacity-40',
                    )}
                  >
                    {t(item.label as keyof typeof t)}
                  </Typography>
                </Col>
              </Link>
            )
          })}
        </Row>
      </Col>
      <LinearBlur
        steps={20}
        // The blur radius of the blur in pixels at the peak of the gradient. Default is 64.
        strength={64}
        // How much of the blur is falloff. 0 means no falloff, 100 means the entire blur is falloff. Default is 100.
        falloffPercentage={100}
        // The tint applied to the blur. This can be any valid CSS color. Default is transparent.
        tint='rgba(0, 0, 0, 0.6)'
        // You can pass any div props to the component. Useful for positioning.
        style={{
          position: 'fixed',
          height: '200px',
          width: '100%',
          bottom: -60,
        }}
        side='bottom'
      />
    </Transition>
  )
})

MenuBar.displayName = 'MenuBar'
